import React, { useRef } from 'react';
import { ColumnContainer, ColumnResizer } from './OperationListView.styles';
import { useStowageStore } from 'src/context/StowageContext';

interface OperationColumnProps {
    columnName: string;
    className?: string;
    title?: string;
    onClick?: () => void;
    showResizer?: boolean;
    children?: React.ReactNode;
    onDragStart?: (e: React.DragEvent<HTMLSpanElement>) => void;
    onDragOver?: (e: React.DragEvent<HTMLSpanElement>) => void;
    onDragEnd?: () => void;
    onDrop?: (e: React.DragEvent<HTMLSpanElement>) => void;
    isDraggable?: boolean;
    isActive?: boolean;
    'data-column-index'?: number;
    'data-is-dragging'?: string;
}

// Constants
const MIN_COLUMN_WIDTH = 100;

const OperationColumnComponent: React.FC<OperationColumnProps> = ({
    columnName,
    className = '',
    title,
    onClick,
    showResizer,
    children,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    isDraggable = true,
    isActive = false,
    'data-column-index': dataColumnIndex,
    'data-is-dragging': dataIsDragging = 'false',
}) => {
    const resizerRef = useRef<HTMLDivElement>(null);
    const isResizingRef = useRef<boolean>(false);
    const { operationListColumnWidths, setOperationListColumnWidths } = useStowageStore();

    const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const column = e.currentTarget.parentNode as HTMLElement;
        const startX = e.clientX;
        const startWidth = column.getBoundingClientRect().width;

        resizerRef.current?.classList.add('resizing');

        const handleMouseMove = (mouseMoveEvent: MouseEvent) => {
            const newWidth = startWidth + mouseMoveEvent.clientX - startX;
            if (newWidth > MIN_COLUMN_WIDTH) {
                // Temporarily set pixel width during resize for visual feedback
                column.style.width = `${newWidth}px`;
                column.style.flex = 'none';
            }
        };

        const handleMouseUp = (mouseUpEvent: MouseEvent) => {
            mouseUpEvent.stopPropagation();
            resizerRef.current?.classList.remove('resizing');
            isResizingRef.current = true;

            // Calculate new width and convert to flex value (preserve pixel widths for manual resizing)
            const finalWidth = column.getBoundingClientRect().width;
            const newFlexValue = `${Math.round(finalWidth)}px`;

            // Reset flex styling and let the new flex value take effect
            column.style.width = '';
            column.style.flex = '';

            setOperationListColumnWidths({
                ...operationListColumnWidths,
                [columnName]: newFlexValue,
            });

            // Save to localStorage
            localStorage.setItem(
                'operationListColumnWidths',
                JSON.stringify({
                    ...operationListColumnWidths,
                    [columnName]: newFlexValue,
                })
            );

            // Clear the resize flag after a short delay to prevent immediate click events
            setTimeout(() => {
                isResizingRef.current = false;
            }, 50);

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleDragStart = (e: React.DragEvent<HTMLSpanElement>) => {
        if (!isDraggable) return;
        e.dataTransfer.setData('columnName', columnName);
        e.dataTransfer.effectAllowed = 'move';
        if (onDragStart) onDragStart(e);
    };

    const handleDragOver = (e: React.DragEvent<HTMLSpanElement>) => {
        if (!isDraggable) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (onDragOver) onDragOver(e);
    };

    const handleDrop = (e: React.DragEvent<HTMLSpanElement>) => {
        if (!isDraggable) return;
        e.preventDefault();
        if (onDrop) onDrop(e);
    };

    const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        // Prevent click if we just finished resizing
        if (isResizingRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (onClick) onClick();
    };

    return (
        <ColumnContainer
            className={className}
            width={operationListColumnWidths[columnName]}
            onClick={handleClick}
            title={title}
            data-column-name={columnName}
            data-column-index={dataColumnIndex}
            data-is-dragging={dataIsDragging}
            draggable={isDraggable}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={onDragEnd}
            onDrop={handleDrop}
            isActive={isActive}
        >
            {children}
            {showResizer && <ColumnResizer ref={resizerRef} onMouseDown={startResize} />}
        </ColumnContainer>
    );
};

export default OperationColumnComponent;
